import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { supabase } from '../../core/supabase/supabase.client';

export type PayoutRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'paid'
  | 'ALL';

export interface AdminPayoutRequest {
  id: number;
  rider_id: string;
  amount: number;
  status: PayoutRequestStatus;
  approved_by?: string | null;
  approved_at?: string | null;
  paid_at?: string | null;
  remarks?: string | null;
  transaction_reference?: string | null;
  created_at: string;
  updated_at?: string | null;
  wallet_balance?: number;
  rider?: {
    full_name?: string;
    code?: string;
    phone?: string;
    store_id?: number;
  };
}

export interface PayoutListFilters {
  status?: PayoutRequestStatus;
  riderId?: string;
  fromDate?: string;
  toDate?: string;
  storeId?: number;
  page?: number;
  pageSize?: number;
}

export interface PayoutListResponse {
  data: AdminPayoutRequest[];
  count: number;
  totals: {
    requested: number;
    pending: number;
    approved: number;
    paid: number;
    rejected: number;
  };
}

@Injectable({ providedIn: 'root' })
export class PayoutRequestsService {
  list(filters: PayoutListFilters = {}): Observable<PayoutListResponse> {
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 20;
    const offset = (page - 1) * pageSize;

    return from(
      supabase
        .rpc('admin_get_payout_requests', {
          p_status:
            filters.status && filters.status !== 'ALL' ? filters.status : null,
          p_rider_id: filters.riderId ?? null,
          p_from_date: filters.fromDate ?? null,
          p_to_date: filters.toDate ?? null,
          p_store_id: filters.storeId ?? null,
          p_limit: pageSize,
          p_offset: offset,
        })
        .then(({ data, error }) => {
          if (error) throw error;
          const payload = (data ?? {}) as {
            data?: AdminPayoutRequest[];
            count?: number;
            totals?: PayoutListResponse['totals'];
          };

          return {
            data: payload.data ?? [],
            count: payload.count ?? 0,
            totals: payload.totals ?? {
              requested: 0,
              pending: 0,
              approved: 0,
              paid: 0,
              rejected: 0,
            },
          };
        })
    );
  }

  approve(requestId: number): Observable<void> {
    return from(
      supabase
        .rpc('admin_approve_payout_request', { p_request_id: requestId })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  reject(requestId: number, remarks: string): Observable<void> {
    return from(
      supabase
        .rpc('admin_reject_payout_request', {
          p_request_id: requestId,
          p_remarks: remarks,
        })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }

  markPaid(
    requestId: number,
    transactionReference: string,
    remarks?: string
  ): Observable<void> {
    return from(
      supabase
        .rpc('admin_mark_payout_paid', {
          p_request_id: requestId,
          p_transaction_reference: transactionReference,
          p_remarks: remarks ?? null,
        })
        .then(({ error }) => {
          if (error) throw error;
        })
    );
  }
}
